import { BrandfolderHexColors, ButtonLooks, StandardButton } from '@brandfolder/react';
import classNames from 'classnames';
import Link from 'next/link';
import {
  FunctionComponent,
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { Toaster } from 'react-hot-toast';
import styled from 'styled-components';

import { DragAndDropContext } from '@context/drag-and-drop';
import { PermissionLevelContext } from '@context/permission-level';
import { ThemeContext } from '@context/theme';
import { useDragged } from '@hooks/use-dragged';
import { isKetchDisabled } from '@utilities/ketch';

interface MainProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const CustomPrivacyBanner = () => {
  return (
    <PrivacyBannerWrapper>
      <div className="container">
        <p>
          This site is powered by Brandfolder by Smartsheet. We want you to know that we respect
          your privacy and your right to know how we collect, use, and share your personal data.
          Please read the{' '}
          <Link target="_blank" href="https://www.smartsheet.com/legal/privacy">
            Smartsheet Privacy Notice
          </Link>{' '}
          to learn more about Smartsheet’s privacy practices.
        </p>
        <p>
          Databricks may also have access to and use some personal data collected on this site.
          Please read the{' '}
          <Link target="_blank" href="https://www.databricks.com/legal/privacynotice">
            Databricks Privacy Notice
          </Link>{' '}
          to learn more about Databricks’ privacy practices.
        </p>
      </div>
    </PrivacyBannerWrapper>
  );
};

const UnstyledMain: FunctionComponent<MainProps> = (props) => {
  const { children, ...otherProps } = props;

  const { dragged, setDragged } = useContext(DragAndDropContext);
  const { isLight } = useContext(ThemeContext);
  const { editable } = useContext(PermissionLevelContext);
  const { draggedProps } = useDragged({ dragged, setDragged });
  const [disableKetch, setDisableKetch] = useState(false);
  useEffect(() => {
    setDisableKetch(isKetchDisabled());
  }, []);

  const [className, setClassName] = useState('dark-toast');

  useEffect(() => {
    setClassName(isLight ? 'light-toast' : 'dark-toast');
  }, [isLight]);

  return (
    <main
      {...otherProps}
      {...draggedProps}
      className={classNames({
        main: true,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      id="main"
    >
      {children}
      {disableKetch ? (
        <CustomPrivacyBanner />
      ) : (
        <StandardButton
          className="ketch"
          look={ButtonLooks.Default}
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (window.semaphore) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.semaphore.push(['showPreferences', { showRightsTab: false }]);
            }
          }}
          type="button"
        >
          Cookie Preferences
        </StandardButton>
      )}
      {editable && (
        <Toaster
          containerClassName="toaster"
          position="bottom-center"
          toastOptions={{
            className: className
          }}
        />
      )}
    </main>
  );
};

const StyledMain = styled(UnstyledMain)`
  grid-area: main;
  min-width: 0;
  position: relative;

  .ketch {
    bottom: 12px;
    left: 12px;
    min-height: 0;
    padding: 6px 8px;
    position: absolute;
    z-index: 2;
  }

  .light-toast {
    background-color: ${BrandfolderHexColors.White};
    color: ${BrandfolderHexColors.Text};
    font-family: 'Circular-Medium';
    font-weight: 500;
  }

  .dark-toast {
    background-color: ${BrandfolderHexColors.Title};
    color: ${BrandfolderHexColors.White};
    font-family: 'Circular-Medium';
    font-weight: 500;
  }
`;

const PrivacyBannerWrapper = styled.div`
  background: #0b2026;
  color: #eeede9;
  width: 100%;
  position: relative;
  z-index: 200;

  .container {
    max-width: 1120px;
    margin: 0 auto;
    text-align: center;
    font-size: 12px;
    font-family: 'DM Sans', Arial, sans-serif;
    padding: 30px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  a {
    color: #eeede9;
  }
`;

export const Main: FunctionComponent<MainProps> = (props) => {
  return <StyledMain {...props} />;
};
